// Percorso file: src/pages/home/HomeAdmin.jsx
//
// Home del ruolo ADMIN — completata (cantiere HOME-ADMIN).
//
// È la sorella della home del SuperAdmin (HomeSuperadmin.jsx): stesso
// impianto, stessa forma, stesso ritmo. DIECI voci nel MENU, poi lo Shop
// e, in coda a tutto, «More / Assistance»: tutte pulsanti oro (doc 07
// §5.1) con icona Font Awesome a sinistra (§5: emoji abolite, regola
// tutto-o-niente). Unica variante d'accento della pagina: il Shop (§5.1:
// al massimo una per pagina).
//
// NESSUNA VOCE È PIÙ SPENTA. «Clinic / Off Season / Specials» era l'ultima
// («coming soon», `<button disabled>`, flag `wip`): dal cantiere CLINICS
// (Punto 6, 15/9/2026) è ACCESA e naviga a /clinic-events, la plancia di
// gestione degli eventi clinic (ClinicEvents.jsx, gate ADMIN/SUPERADMIN in
// App.jsx dalla Fetta B). Ha la forma delle altre voci vive — `to` nella
// tabella e nient'altro. Il ramo `wip` del `.map`, rimasto senza voci che
// lo usassero, è stato tolto.
// La voce in coda a tutto, resa a mano sotto lo Shop, era la seconda
// spenta («More / Assistance — coming soon»): dal cantiere LEGAL-DOCS è
// ACCESA e naviga a /more, la pagina-CONTENITORE (MorePage.jsx) che apre
// le voci dei documenti — «Legal & Privacy» (/legal), «Terms & Conditions»
// (/terms) e «Member Protection» (/legal/mpio); la stessa voce sta in coda
// anche alle home PLAYER, PARENT e COACH. L'etichetta resta «More /
// Assistance», perché il pulsante è il contenitore. Ha la forma delle
// voci vive del `.map` (`onClick={() => navigate(...)}`), ma resta fuori
// dalla tabella MENU perché deve stare in coda a tutto, sotto lo Shop.
// «Coach Attendance» (cantiere COACH-ATTENDANCE, Fetta 2, 16/9/2026)
// naviga a /coach-attendance, la pagina con cui l'amministratore segna le
// presenze degli ALLENATORI (CoachAttendance.jsx, gate ADMIN/SUPERADMIN in
// App.jsx). Ha la forma delle altre voci vive — `to` nella tabella e
// nient'altro — e sta accanto a Team Assignment, che è la pagina delle
// squadre: la posizione esatta la conferma il committente.
// «Teams» (cantiere TEAMS-LIST, E1, 25/9/2026) naviga a /teams-list, la
// pagina di SOLA CONSULTAZIONE delle squadre dell'annata (TeamsList.jsx,
// gate ADMIN/SUPERADMIN in App.jsx): sta subito SOTTO Team Assignment e
// sopra Coach Attendance, per decisione del committente.
// «Member Reminders» (cantiere MEMBER-REMINDERS, Fase 1, 25/9/2026) naviga a
// /reminders, la pagina con cui l'amministratore spedisce a mano i solleciti
// ai soci (Reminders.jsx, gate ADMIN/SUPERADMIN in App.jsx): oggi il solo
// tipo «Complete your data». Sta subito SOTTO «Teams» e sopra Coach
// Attendance, per decisione del committente (D1).
// «Manage User Contexts» è attiva anche per l'ADMIN
// (cantiere ADMIN-MUC) e naviga a /user-context-manager, come nella home
// del SuperAdmin.
//
// ⚠️ «LIVE CHAT» È TORNATA ACCESA (cantiere LIVE CHAT): naviga di nuovo
// a /messaging, l'indirizzo che aveva prima che la navigazione le fosse
// tolta. La destinazione ora c'è: la pagina Messaging è ricostruita e la
// rotta è dietro <PrivateRoute> (App.jsx). La voce riprende la forma
// delle altre voci vive — `to` nella tabella e nient'altro.
//   ⚠️ SEQUENZA DEL TRAVASO IN PRODUZIONE: questa accensione è per il
//   COLLAUDO IN BETA (dati finti). In produzione il bottone va acceso
//   INSIEME al deploy delle regole di sicurezza della chat (il recinto),
//   MAI PRIMA: un socio non deve poter entrare in una chat il cui recinto
//   non è ancora attivo.
//
// ⚠️ «CALENDAR — EVENTS» È TORNATA ACCESA (cantiere CALENDAR, fetta E):
// naviga di nuovo a /calendar, l'indirizzo che aveva prima che la
// navigazione le fosse tolta. La destinazione ora c'è: la rotta è dietro
// <PrivateRoute> (App.jsx), e le fette A/B/C del cantiere hanno chiuso il
// backend degli eventi, riservato la scrittura agli admin e rifatto la
// «My Week». La voce riprende la forma delle altre voci vive — `to` nella
// tabella e nient'altro — perché è `to` a distinguere una voce viva da una
// spenta.
//
// DIFFERENZE dalla home del SuperAdmin, tutte qui:
//   - NIENTE «Finance & Cash Management»: la voce è del SuperAdmin sola;
//   - il titolo è «Admin».
// Tutto il resto è identico: riga social, Shop d'accento, banner di
// chiusura, NIENTE My Profile e NIENTE foto/AuthAvatar sopra il titolo.
//
// Il controllo d'accesso NON sta qui: sta in App.jsx, nel cancello
// condiviso RequireRole. Questa è una home di primo livello, quindi
// nessun «Back» nella banda (doc 07 §7.2).

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaComments,
  FaUsers,
  FaCalendarAlt,
  FaSitemap,
  FaUserFriends,
  FaClipboardCheck,
  FaUserCog,
  FaTshirt,
  FaFutbol,
  FaLifeRing,
  FaShoppingBag,
  FaEnvelope,
} from "react-icons/fa";
import HomeShell from "../../components/HomeShell";
import { SOCIAL, SHOP_URL } from "../../components/clubLinks";
// DOPO il telaio, e l'ordine conta: HomeShell porta con sé HomeShell.css,
// e questo foglio ne ritocca una regola (il margine di una voce del
// menu). Se salisse sopra, il telaio lo sovrascriverebbe.
import "./HomeAdmin.css";

// Le dieci voci interne, nell'ordine deciso dall'utente. Il separatore
// delle voci composte è il trattino lungo con spazi (doc 07 §3.3).
//
// Ogni voce porta `to` e naviga lì. La chiave della lista è l'etichetta.
//
// `spaced: true` è il DOPPIO RESPIRO (HomeAdmin.css): sta sulla voce che
// APRE il gruppo che si stacca, mai in coda a quella che lo precede. Lo
// porta la sola User List Dashboard, e serve a isolare in cima la Live
// Chat che la precede.
const MENU = [
  // ACCESA per il collaudo in beta (cantiere LIVE CHAT). Al travaso in
  // produzione l'accensione va INSIEME al deploy delle regole della chat
  // (il recinto), mai prima. Vedi l'avvertenza in testa al file.
  { label: "Live Chat", to: "/messaging", Icon: FaComments },
  { label: "User List Dashboard", to: "/users", Icon: FaUsers, spaced: true },
  { label: "Calendar — Events", to: "/calendar", Icon: FaCalendarAlt },
  { label: "Team Assignment", to: "/teams", Icon: FaSitemap },
  { label: "Teams", to: "/teams-list", Icon: FaUserFriends },
  { label: "Member Reminders", to: "/reminders", Icon: FaEnvelope },
  { label: "Coach Attendance", to: "/coach-attendance", Icon: FaClipboardCheck },
  { label: "Manage User Contexts", to: "/user-context-manager", Icon: FaUserCog },
  { label: "Uniform Distribution", to: "/uniforms", Icon: FaTshirt },
  { label: "Clinic / Off Season / Specials", to: "/clinic-events", Icon: FaFutbol },
];

/*
 * I quattro canali del club (`SOCIAL`) e l'indirizzo del negozio
 * (`SHOP_URL`) NON vivono qui: stanno in `components/clubLinks.js`,
 * perché non sono di questa home — li mostrano anche la home del
 * SuperAdmin e quella del genitore. Si importano in testa al file.
 */

const HomeAdmin = () => {
  const navigate = useNavigate();

  return (
    <HomeShell>
      <h1 className="qpsc-page-title">Admin</h1>

      <div className="qpsc-social">
        {SOCIAL.map(({ href, title, Icon }) => (
          <a
            key={title}
            className="qpsc-social__link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            aria-label={title}
          >
            <Icon />
          </a>
        ))}
      </div>

      <nav className="qpsc-menu">
        {MENU.map(({ label, to, Icon, spaced }) => (
          <button
            key={label}
            type="button"
            className={"qpsc-btn-gold" + (spaced ? " qpsc-admin-spaced" : "")}
            onClick={() => navigate(to)}
          >
            <span className="qpsc-btn-gold__icon">
              <Icon />
            </span>
            {label}
          </button>
        ))}

        {/* Unica voce esterna, e unica variante d'accento della pagina.
            Porta il DOPPIO RESPIRO in testa (HomeAdmin.css): è reso a
            mano, fuori dal `.map`, quindi la classe si scrive qui e non
            passa dal flag `spaced` del MENU. Il margine lo stacca dalle
            dieci voci che lo precedono. */}
        <a
          className="qpsc-btn-gold qpsc-btn-gold--accent qpsc-admin-spaced"
          href={SHOP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="qpsc-btn-gold__icon">
            <FaShoppingBag />
          </span>
          QPSC Online Shop
        </a>

        {/* «More / Assistance», resa a mano SOTTO lo Shop, fuori dal `.map`,
            perché deve stare in coda a tutto: stesso markup del ramo vivo
            qui sopra (stesse classi, `onClick` che naviga), nessuna classe
            d'accento, nessun respiro doppio — il respiro doppio è dello
            Shop, e questa voce gli sta accanto, al `gap` normale della
            colonna. Era spenta («coming soon»): accesa dal cantiere
            LEGAL-DOCS verso il contenitore /more, da cui si scende a
            «Legal & Privacy» e «Terms & Conditions». */}
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/more")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaLifeRing />
          </span>
          More / Assistance
        </button>
      </nav>

      {/* BANNER DI CHIUSURA, in coda dopo il menu. La resa è del telaio,
          `.qpsc-adbanner` (HomeShell.css): una classe sola per tutte le
          home che lo mostrano, e qui NON si crea nessun CSS proprio. NON
          è cliccabile: nessun `<a>`, nessun `onClick`, nessun `role` — è
          un'immagine e basta, e sta FUORI dal `<nav>` proprio perché non
          è una voce di navigazione. L'immagine è servita da `public/`,
          quindi non si importa: il percorso è assoluto e l'URL è
          CODIFICATO, perché il file sul disco si chiama «footer AG.png»,
          con uno spazio nel nome, e in un URL lo spazio è `%20`. */}
      <img
        className="qpsc-adbanner"
        src="/assets/footer%20AG.png"
        alt="AustraliaGoal"
      />
    </HomeShell>
  );
};

export default HomeAdmin;
